import { createFileRoute, Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { ChevronRightIcon, EllipsisVerticalIcon } from 'lucide-react'
import { NavStack, NavStackStage, NavStackScreen } from '@/components/custom/route-transition/stack/nav-stack'
import { usePreviewMode } from '@/components/custom/route-transition/surface/route-preview'
import { Button } from '@/components/ui/button'
import { SlideInPanel, ScrollableArea } from './-components/shared/layout'
import { burnCommitCost, ItemScreenTitle, useCommitCost } from './-components/dev/transition-playground'

// The HIERARCHICAL member of the transition playground: a two-level nav stack exactly like a section's — level 0
// an item list, level 1 the pushed item screen, which hosts the INNER lateral pager.
//
// LEVEL 1 IS BUILT LIKE A SECTION'S, NOT LIKE A DEMO'S: a screen that outlives its route (header + pane wrapper
// that only empty when the route commit lands, 100ms+ after the stack hands over), a header with real content in
// it (on Jobs the pop artefact WAS the action menu), the section's nesting inside `SlideInPanel`, and a commit
// that costs something (`burnCommitCost`). Without all four the device-reported pop artefact cannot appear here.
export const Route = createFileRoute('/_app/dev-resources/transitions/one')({
  component: RouteComponent,
})

const ITEM_OPEN = /\/transitions\/one\/item(\/|$)/

/**
 * Is the item screen the RENDERED route? Read from the matched routes, which is the same timing a section gets
 * from `useParams({ strict: false })` — both come from `state.matches`, so both flip on the route COMMIT and
 * not a moment before. That timing is the thing being reproduced: it is what leaves a popped level full of
 * content while the stack has already moved on.
 */
function useItemOpen(): boolean {
  return useRouterState({ select: (state) => state.matches.some((match) => ITEM_OPEN.test(match.pathname)) })
}

function ItemList(props: { itemOpen: boolean }) {
  // The list is what a pop commits INTO, so the cost of that commit belongs here — the same place a section
  // pays it, re-rendering its rows because the selection changed. It takes `itemOpen` for exactly that reason:
  // a component that depends on nothing is memoized by React Compiler and skipped by the commit that matters.
  burnCommitCost(useCommitCost())
  return (
    <div className="flex flex-col gap-2 p-4">
      <p className="text-muted-foreground text-sm">
        Level 0 — the stack root{props.itemOpen ? ' (an item is open above it)' : ''}. A row pushes the item screen,
        whose tabs are the inner lateral pager.
      </p>
      {Array.from({ length: 3 }, (_, i) => (
        <Link
          key={i}
          to="/dev-resources/transitions/one/item"
          className="hover:bg-accent flex items-center justify-between rounded-md border p-3 text-sm"
        >
          Playground item {i + 1}
          <ChevronRightIcon className="text-muted-foreground size-4" />
        </Link>
      ))}
    </div>
  )
}

/** The level's trailing header controls — the shape of Jobs' `JobScreenActions`, which is what flashed. */
function ItemScreenActions() {
  return (
    <Button variant="ghost" size="icon" aria-label="Item actions" className="size-8">
      <EllipsisVerticalIcon className="size-4" />
    </Button>
  )
}

function RouteComponent() {
  const navigate = useNavigate()
  const itemOpen = useItemOpen()
  // Live previews route through here too: the INNER pager's preview is of a surface BELOW us, so fall through to
  // the item layout's own gate.
  //
  // An `above` preview renders the WHOLE LEVEL, chrome included — the same thing every section does — because a
  // level's header is CONTENT, not chrome-around-a-pane. Returning the bare `<ItemList/>` showed the list with no
  // header for a whole swipe from Two, 48px out of place, with the title appearing at the reveal.
  const preview = usePreviewMode('/dev-resources/transitions/one')
  if (preview === 'passthrough') return <Outlet />
  return (
    // The playground carries the real thing, eager push included — it is where the stack is driven by hand.
    <NavStack levels={[true, ITEM_OPEN]} onPop={() => void navigate({ to: '/dev-resources/transitions/one' })}>
      {/* On a phone the stack's layers are `position: absolute; inset: 0` — the stage must own a real
          height (the section layouts get theirs from the app shell; this playground sets its own). */}
      <NavStackStage className="flex w-full overflow-hidden max-md:h-[65dvh]" style={{ containerType: 'inline-size' }}>
        <NavStackScreen index={0} title="Playground items">
          <ItemList itemOpen={itemOpen} />
        </NavStackScreen>
        {/* The section's own nesting, verbatim: the desktop grouping wrapper, which `ns-passthrough` turns into
            no box at all on mobile so the level inside positions against the stage. */}
        <SlideInPanel isOpen={itemOpen} innerClassName="flex flex-col" className="ns-passthrough">
          <NavStackScreen
            index={1}
            title={<ItemScreenTitle open={itemOpen} />}
            actions={itemOpen ? <ItemScreenActions /> : null}
          >
            {/* Gated on the RENDERED route, like every section: the pane empties on the route commit, which is
                a task (and on a phone a long one) after the pop handed the stack over. */}
            <ScrollableArea>{itemOpen && <Outlet />}</ScrollableArea>
          </NavStackScreen>
        </SlideInPanel>
      </NavStackStage>
    </NavStack>
  )
}
